"use client"

import { useRouter } from "next/navigation"
import { Layers } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select"

interface CategorySelectProps {
  categories: Array<{
    name: string
    slug: string
    count: number
  }>
  currentSlug: string
  currentName: string
  currentCount?: number // Actual count from page (more reliable than categories array lookup)
}

export function CategorySelect({ categories, currentSlug, currentName, currentCount }: CategorySelectProps) {
  const router = useRouter()

  const handleChange = (value: string) => {
    if (value === "all") {
      router.push("/blog")
    } else {
      router.push(`/blog/category/${value}`)
    }
  }

  // Use passed currentCount if available, otherwise fall back to categories lookup
  const displayCount = currentCount ?? categories.find(c => c.slug === currentSlug)?.count ?? 0

  return (
    <Select value={currentSlug} onValueChange={handleChange}>
      <SelectTrigger className="w-full bg-white border border-gray-200 rounded-lg px-3 py-3 h-auto text-sm font-medium text-gray-900 focus:ring-2 focus:ring-green-500 focus:border-green-500 shadow-xs hover:border-gray-300 transition-colors [&>svg]:hidden">
        <div className="flex items-center gap-2 w-full">
          <Layers className="w-4 h-4 text-green-600 flex-shrink-0" />
          <span className="flex-1 text-left truncate">
            {currentName} ({displayCount})
          </span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 flex-shrink-0"><path d="m6 9 6 6 6-6"/></svg>
        </div>
      </SelectTrigger>
      <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg overflow-hidden">
        <SelectItem
          value="all"
          className="py-2.5 text-sm cursor-pointer hover:bg-green-50 focus:bg-green-50 focus:text-green-700 data-[state=checked]:bg-green-100 data-[state=checked]:text-green-700"
        >
          All Articles
        </SelectItem>
        {categories.map((cat) => (
          <SelectItem
            key={cat.slug}
            value={cat.slug}
            className="py-2.5 text-sm cursor-pointer hover:bg-green-50 focus:bg-green-50 focus:text-green-700 data-[state=checked]:bg-green-100 data-[state=checked]:text-green-700"
          >
            {cat.name} <span className="text-gray-400">({cat.count})</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
